import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme, type ThemePref } from '../../lib/theme';

const SUN = 'M12 4V2M12 22v-2M4 12H2M22 12h-2M5.6 5.6L4.2 4.2M19.8 19.8l-1.4-1.4M5.6 18.4l-1.4 1.4M19.8 4.2l-1.4 1.4M16 12a4 4 0 11-8 0 4 4 0 018 0z';
const MOON = 'M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z';
const SYS = 'M3 5h18v11H3zM8 20h8M12 16v4';
const OPTS: { v: ThemePref; label: string; d: string }[] = [
  { v: 'light', label: 'Light', d: SUN },
  { v: 'dark', label: 'Dark', d: MOON },
  { v: 'system', label: 'System', d: SYS },
];

/** Light / Dark / System segmented switch (menu, settings). */
export default function ThemeSwitch({ id }: { id?: string }) {
  const { pref, setPref } = useTheme();
  return (
    <div className="thm" role="radiogroup" aria-label="Theme" id={id}>
      {OPTS.map((o) => (
        <button key={o.v} type="button" role="radio" aria-checked={pref === o.v} onClick={() => setPref(o.v)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d={o.d} /></svg>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Compact theme menu for the signed-out header: icon button that opens the same switch. */
export function ThemeMenu() {
  const { theme } = useTheme();
  const ref = useRef<HTMLDetailsElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    if (ref.current) ref.current.open = false;
  }, [pathname]);
  useEffect(() => {
    const out = (e: MouseEvent) => {
      const d = ref.current;
      if (d?.open && !d.contains(e.target as Node)) d.open = false;
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && ref.current?.open) ref.current.open = false;
    };
    document.addEventListener('click', out);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('click', out);
      document.removeEventListener('keydown', esc);
    };
  }, []);
  return (
    <details className="more thm-m" ref={ref}>
      <summary aria-label="Theme: light, dark or system">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d={theme === 'dark' ? MOON : SUN} /></svg>
      </summary>
      <div className="mm">
        <p className="thm-h">Theme</p>
        <ThemeSwitch />
      </div>
    </details>
  );
}
